import React from 'react';
import PropTypes from 'prop-types';
import AiFeatures from './ai/AiFeatures';
import { Ic } from '../shell/Icons';

// Bottom action bar — matches the agreed mockup `.actionbar` (Save primary,
// the rest secondary/ghost) while keeping every action + the test ids.
// #1031: also the destination for Approve/Unapprove and Record verification,
// moved down from the ItemDetail status bar (see the two blocks below).
const ItemDetailsToolbar = ({
  hasChanges,
  loading,
  handleSave,
  handleCancel,
  handleDelete,
  handleLink,
  handleOpenAddDialog,
  showApproval,
  approvalLabel,
  approvalTooltip,
  handleToggleApproval,
  showVerification,
  handleOpenVerification,
  aiFeatures,
  calculations,
}) => {
  return (
    <div className="actionbar actionbar-sticky">
      <button type="button"
        className="btn primary"
        onClick={handleSave}
        disabled={!hasChanges || loading}
        data-testid="item-save"
      >
        <Ic name="check" />{loading ? 'Saving…' : 'Save'}
      </button>
      <button type="button"
        className="btn secondary"
        onClick={handleCancel}
        disabled={!hasChanges || loading}
        data-testid="item-cancel"
      >
        Cancel
      </button>
      <button type="button" className="btn secondary" onClick={handleDelete} disabled={loading} data-testid="item-delete" aria-label="Delete this item">
        Delete
      </button>
      <button type="button" className="btn secondary" onClick={handleLink} data-testid="item-link" aria-label="Link this item to another">
        <Ic name="link" />Link
      </button>

      {/* #1031: Approve/Unapprove and Record verification — moved down here from the
          ItemDetail status bar, where they'd been squeezed into 18px chip-height pills
          that wrapped/clipped at anything less than a very wide desktop viewport. Styled
          like every other button in this bar (`.btn.secondary`) rather than re-inventing
          a look; gating is unchanged (leaf-only for Approve, verifiable-type-only for
          Record verification) and both keep the SAME data-testid so existing locators
          still resolve. */}
      {showApproval && (
        // WCAG 2.5.3 (Label in Name): the visible text IS "Approve"/"Unapprove", so the
        // explanatory sentence goes on `title` only (a tooltip + accessible DESCRIPTION,
        // via the button's own text content) rather than `aria-label` — an aria-label here
        // would replace the accessible NAME with that sentence and break any locator/
        // assistive tech relying on the visible word, which is exactly what happened to
        // this button when it briefly carried one.
        <button type="button"
          className="btn secondary"
          onClick={handleToggleApproval}
          disabled={loading}
          data-testid="approve-toggle"
          title={approvalTooltip}
        >
          <Ic name="check" />{approvalLabel}
        </button>
      )}
      {showVerification && (
        <button type="button"
          className="btn secondary"
          onClick={handleOpenVerification}
          disabled={loading}
          data-testid="verification-record-open"
        >
          <Ic name="shield" />Record verification
        </button>
      )}

      {/* AI tools dropdown (keeps its own menu/behaviour) */}
      <AiFeatures {...aiFeatures} onCalculateLimitsClick={calculations?.handleCalculateLimits} />

      <button type="button" className="btn secondary" onClick={() => handleOpenAddDialog(false)} data-testid="item-new-requirement" aria-label="Create a new requirement">
        <Ic name="plus" />New Requirement
      </button>
      <button type="button" className="btn secondary" onClick={() => handleOpenAddDialog(true)} data-testid="item-new-container" aria-label="Create a new container">
        <Ic name="folder" />New Container
      </button>
    </div>
  );
};

ItemDetailsToolbar.propTypes = {
  hasChanges: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleLink: PropTypes.func.isRequired,
  handleOpenAddDialog: PropTypes.func.isRequired,
  showApproval: PropTypes.bool,
  approvalLabel: PropTypes.string,
  approvalTooltip: PropTypes.string,
  handleToggleApproval: PropTypes.func,
  showVerification: PropTypes.bool,
  handleOpenVerification: PropTypes.func,
  aiFeatures: PropTypes.object.isRequired,
  calculations: PropTypes.object,
};

export default ItemDetailsToolbar;
